import React, { useEffect, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import { supabase } from '../services/supabase';

type Status = 'loading' | 'form' | 'success' | 'error';

const ResetPasswordPage: React.FC = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let resolved = false;

    // Check for error parameters in URL (e.g., expired token)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    const urlError = hashParams.get('error') || queryParams.get('error');
    const errorCode = hashParams.get('error_code') || queryParams.get('error_code');
    const errorDescription = hashParams.get('error_description') || queryParams.get('error_description');

    if (urlError) {
      resolved = true;
      if (errorCode === 'otp_expired') {
        setErrorMessage('This password reset link has expired. Please request a new one from the app.');
      } else {
        setErrorMessage(errorDescription || 'This password reset link is invalid or has expired.');
      }
      setStatus('error');
      return;
    }

    // Check that we have some auth params
    const code = queryParams.get('code');
    const hasHash = window.location.hash && window.location.hash.includes('access_token');
    if (!code && !hasHash) {
      resolved = true;
      setErrorMessage('No password reset token found in URL.');
      setStatus('error');
      return;
    }

    // Timeout: if nothing happens in 10 seconds, show error
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        setErrorMessage('Validation timed out. The link may be invalid or expired.');
        setStatus('error');
      }
    }, 10000);

    // Listen for Supabase auth events — it auto-processes the hash fragment
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (resolved) return;
      if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session) {
        resolved = true;
        clearTimeout(timeoutId);
        setStatus('form');
      }
    });

    // For PKCE flow (?code=), exchange manually
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then((result) => {
        if (resolved) return;
        if (!result.error && result.data.session) {
          resolved = true;
          clearTimeout(timeoutId);
          setStatus('form');
        } else {
          resolved = true;
          clearTimeout(timeoutId);
          setErrorMessage(result.error?.message || 'Failed to validate reset link.');
          setStatus('error');
        }
      }).catch(() => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          setErrorMessage('Failed to validate reset link.');
          setStatus('error');
        }
      });
    }

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters long.');
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setFormError(error.message || 'Failed to reset password. Please try again.');
        setSubmitting(false);
      } else {
        setStatus('success');
      }
    } catch {
      setFormError('An error occurred. Please try again.');
      setSubmitting(false);
    }
  };

  const openApp = () => {
    window.location.href = 'golfholeinone://';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 flex justify-center items-center pt-24 pb-16 px-5">
        <div className="bg-white rounded-xl p-10 max-w-md w-full text-center shadow-lg border border-gray-100">

          {/* Loading State */}
          {status === 'loading' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-5">Validating Reset Link...</h1>
              <div className="w-10 h-10 border-3 border-gray-200 border-t-black rounded-full animate-spin mx-auto my-5" />
              <p className="text-gray-500 mt-5 leading-relaxed">Please wait while we verify your password reset link.</p>
            </>
          )}

          {/* Password Reset Form */}
          {status === 'form' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-5">Reset Your Password</h1>
              <p className="text-gray-500 mb-5 leading-relaxed">Enter your new password below.</p>

              <form onSubmit={handleSubmit}>
                <div className="text-left mb-5">
                  <label htmlFor="password" className="block mb-2 text-sm font-semibold text-gray-900">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-black"
                  />
                </div>

                <div className="text-left mb-5">
                  <label htmlFor="confirmPassword" className="block mb-2 text-sm font-semibold text-gray-900">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    placeholder="Confirm new password"
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-black"
                  />
                </div>

                {formError && (
                  <p className="text-red-600 text-sm mt-2 text-left">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-black text-white py-3.5 rounded-lg text-base font-semibold mt-3 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-5">Password Reset Successful!</h1>
              <p className="text-gray-500 mt-5 leading-relaxed">You can now sign in to the app with your new password.</p>
              <button
                onClick={openApp}
                className="w-full bg-black text-white py-3.5 rounded-lg text-base font-semibold mt-5 hover:opacity-90 inline-block no-underline"
              >
                Open Hole In 1 App
              </button>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-5">Invalid or Expired Link</h1>
              <p className="text-red-600 text-sm mt-2">{errorMessage}</p>
              <p className="text-gray-500 mt-5 leading-relaxed">Please request a new password reset from the app.</p>
              <button
                onClick={openApp}
                className="w-full bg-black text-white py-3.5 rounded-lg text-base font-semibold mt-5 hover:opacity-90 inline-block no-underline"
              >
                Open Hole In 1 App
              </button>
            </>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
